import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SentOrderItem } from "@/hooks/useCurrentOrderItems";

interface CancelSentItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: SentOrderItem | null;
  onConfirm: (itemId: string, reason?: string) => Promise<boolean>;
  isCancelling: boolean;
}

export function CancelSentItemDialog({
  open,
  onOpenChange,
  item,
  onConfirm,
  isCancelling,
}: CancelSentItemDialogProps) {
  const [reason, setReason] = useState("");

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleConfirm = async () => {
    if (!item) return;
    const success = await onConfirm(item.id, reason || undefined);
    if (success) {
      setReason("");
      onOpenChange(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReason("");
    }
    onOpenChange(newOpen);
  };

  if (!item) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Cancelar Item
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Deseja cancelar o item abaixo? Esta ação não pode ser desfeita.
              </p>
              <div className="bg-muted rounded-lg p-3 space-y-1">
                <p className="font-medium text-foreground">{item.product_name}</p>
                <p className="text-sm">
                  Quantidade: {item.quantity} • {formatCurrency(Number(item.total_price))}
                </p>
                {item.notes && (
                  <p className="text-sm text-amber-600">Obs: {item.notes}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="cancel-reason">Motivo do cancelamento (opcional)</Label>
                <Input
                  id="cancel-reason"
                  placeholder="Ex: Cliente desistiu, erro de lançamento..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isCancelling}>
            Voltar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={isCancelling}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isCancelling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cancelando...
              </>
            ) : (
              "Confirmar Cancelamento"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
